import React, { useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom'; 
import apiService from "../../../../utils/apiService";
import { CheckIcon } from '@heroicons/react/20/solid';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const Pricing = () => {
    const stripe = useStripe();
    const userEmail = useSelector((state) => state.user.email);
    const userId = useSelector((state) => state.user.id);    
    const loggedIn = useSelector((state) => state.user.loggedIn);
    const userSub = useSelector((state) => state.user.subscription);
    const [sessionLoading, setSessionLoading] = useState("");
    const navigate = useNavigate();

    // Define tiers
    const tiers = [
        {
            name: "Free Trial",
            id: "tier-trial",
            prod: "trial",
            priceId: import.meta.env.VITE_STRIPE_MONTHLY,
            price: "$0",
            priceSuffix: "/month",
            description: "Try out all features for 30 days, then continue with a paid plan.",
            cycle: "30 day trial",
            postCycle: "then $14.99 per month",
            btnText: loggedIn ? "Start Free Trial" : "Create an Account",
            features: [
                "Create private systems",
                "Follow public systems",
                "Publish systems to the Community",
                "Analyze upcoming games",
                "See lines across multiple books",
                "Email notifications for system matches"
            ],
            mostPopular: false,
            hideIfSubscribed: true
        },
        {   
            name: "Monthly",
            id: "tier-monthly",
            prod: "monthly",
            priceId: import.meta.env.VITE_STRIPE_MONTHLY,
            price: "$14.99",
            priceSuffix: "/month",
            description: "Pay month-to-month with no long-term commitment.",
            cycle: "billed monthly",
            btnText: loggedIn ? "Subscribe" : "Create an Account",
            features: [
                "Create private systems",
                "Follow public systems",
                "Publish systems to the Community",
                "Analyze upcoming games",
                "See lines across multiple books",
                "Email notifications for system matches"
            ],
            mostPopular: false
        },
        {
            name: "Annual",
            id: "tier-annual",
            prod: "annual",
            priceId: import.meta.env.VITE_STRIPE_ANNUAL,
            price: "$119.99",
            priceSuffix: "/year",
            description: "Our best value option with significant savings.",
            cycle: "billed annually",
            btnText: loggedIn ? "Subscribe" : "Create an Account",
            features: [
                "Create private systems",
                "Follow public systems",
                "Publish systems to the Community",
                "Analyze upcoming games",
                "See lines across multiple books",
                "Email notifications for system matches"
            ],
            mostPopular: true
        }
    ];

    // Filter out the trial option if user already has a subscription
    const displayedTiers = tiers.filter(tier => {
        if (tier.hideIfSubscribed && userSub !== null) return false;
        return true;
    });

    const handleCheckout = async (tier) => {
        // If user is signed in - create checkout session
        if(userId && userEmail){
            setSessionLoading(tier.prod);

            const body = { 
                priceId: tier.priceId,
                successUrl: `${window.location.origin}/subscribed?session_id={CHECKOUT_SESSION_ID}`,
                cancelUrl: `${window.location.origin}/pricing`,
                userId,
                userEmail,
                trial: tier.prod === "trial"
            };
          
            try{
                const payload = await apiService('payments/checkout-session', "POST", body, {});
                const sessionId = payload.sessionId;
                const result = await stripe.redirectToCheckout({ sessionId });
                if (result.error) {
                    console.error(result.error.message);
                }
            } catch(error) {
                // Handle all other errors
                if(error.status === 401 || error.message === 'Session Expired') {
                    clearSession();
                    navigate('/signin');
                } else if(error.status === 0) {
                    console.error('Network error while creating checkout session:', error.message);
                } else {
                    console.error(`Error creating checkout session (${error.status}):`, error.message);
                }            
            }
            
            return;
        }

        // Navigate to create account page if not signed in
        navigate('/signup');
    };

    return (
        <div className="bg-white dark:bg-brand-dark-900 py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-4xl text-center">
                    <h2 className="text-xl/8 font-semibold text-brand-red-500 dark:text-white">Pricing</h2>
                    <p className="mt-2 text-5xl font-semibold tracking-tight text-balance text-gray-900 dark:text-white sm:text-6xl">
                        Choose the plan that's right for you
                    </p>
                </div>
                <p className="mx-auto mt-6 max-w-2xl text-center text-lg font-medium text-pretty text-gray-600 dark:text-gray-400 sm:text-xl/8">
                    Get access to powerful sports betting analysis tools to make smarter wagers and improve your results.
                </p>
                
                <div className="mx-auto mt-16 flex flex-wrap justify-center gap-8">
                    {displayedTiers.map((tier) => (
                        <div
                            key={tier.id}
                            className={classNames(
                                tier.mostPopular 
                                    ? 'ring-2 ring-brand-red-500 dark:bg-white/5 bg-white' 
                                    : 'ring-1 ring-gray-200 bg-white dark:ring-white/10 dark:bg-transparent',
                                'rounded-3xl p-8 xl:p-10 shadow-lg dark:shadow-gray-800/10 w-full max-w-sm'
                            )}
                        >
                            <div className="flex flex-col h-full">
                                {/* Header section with fixed spacing */}
                                <div className="flex-none">
                                    <div className="flex items-center justify-between gap-x-4">
                                        <h3 id={tier.id} className="text-lg/8 font-semibold text-gray-900 dark:text-white">
                                            {tier.name}
                                        </h3>
                                        {tier.mostPopular && (
                                            <p className="rounded-full bg-brand-red-500 px-2.5 py-1 text-xs/5 font-semibold text-white">
                                                Best value
                                            </p>
                                        )}
                                    </div>
                                    <p className="mt-4 text-sm/6 text-pretty text-gray-600 dark:text-gray-300">{tier.description}</p>
                                    <p className="mt-6 flex items-baseline gap-x-1">
                                        <span className="text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">
                                            {tier.price}
                                        </span>
                                        <span className="text-sm/6 font-semibold text-gray-600 dark:text-gray-300">
                                            {tier.priceSuffix}
                                        </span>
                                    </p>
                                    {tier.postCycle ? (
                                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{tier.postCycle}</p>
                                    ) : (
                                        <div className="mt-1 h-5"></div>
                                    )}
                                </div>

                                {/* Features list with flex-grow to fill space */}
                                <ul role="list" className="mt-8 flex-grow space-y-3 text-sm/6 text-gray-600 dark:text-gray-300">
                                    {tier.features.map((feature) => (
                                        <li key={feature} className="flex gap-x-3">
                                            <CheckIcon 
                                                aria-hidden="true" 
                                                className="h-6 w-5 flex-none text-brand-red-500 dark:text-white" 
                                            />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>

                                {/* Button section with fixed spacing */}
                                <div className="flex-none mt-8">
                                    <button
                                        onClick={() => handleCheckout(tier)}
                                        disabled={sessionLoading === tier.prod}
                                        aria-describedby={tier.id}
                                        className={classNames(
                                            tier.mostPopular
                                                ? 'bg-brand-red-500 text-white shadow-sm hover:bg-brand-red-600 focus-visible:outline-brand-red-500'
                                                : 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:ring-0 dark:focus-visible:outline-white',
                                            'block w-full rounded-md px-3 py-2 text-center text-sm/6 font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-70 disabled:cursor-not-allowed transition-colors'
                                        )}
                                    >
                                        {sessionLoading === tier.prod ? "Processing..." : tier.btnText}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Pricing;